import { API_HOST } from '@/constants/config';

const VALMAR_ORIGIN = 'https://app.valmarmerchant.com';
const DEFAULT_PROBE_TIMEOUT_MS = 4000;
const TOKENIZER_SCRIPT_PATH = '/assets2/js/card_tokenizer.js';
const VALMAR_PUBLIC_API_KEY = 'pub_2cBndTNxOJtMATcyyYNvANS7ssT';

let availabilityCache = null;
let inflightProbe = null;

function probeValmarNetwork(timeoutMs) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(true);
      return;
    }

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => {
      controller?.abort();
      resolve(false);
    }, timeoutMs);

    fetch(`${VALMAR_ORIGIN}/`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller?.signal,
    })
      .then(() => {
        clearTimeout(timer);
        resolve(true);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(false);
      });
  });
}

function probeValmarIframe(timeoutMs) {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(true);
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.title = 'Valmar availability probe';
    iframe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:-9999px';
    iframe.src = `${VALMAR_ORIGIN}/`;

    let settled = false;
    const finish = (available) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      iframe.onload = null;
      iframe.onerror = null;
      iframe.remove();
      resolve(available);
    };

    const timer = setTimeout(() => finish(false), timeoutMs);
    iframe.onload = () => finish(true);
    iframe.onerror = () => finish(false);

    document.body.appendChild(iframe);
  });
}

function loadTokenizerScript() {
  if (typeof window !== 'undefined' && typeof window.Tokenizer === 'function') {
    return Promise.resolve(true);
  }

  if (typeof document === 'undefined') {
    return Promise.resolve(false);
  }

  const scriptUrl = `${API_HOST}${TOKENIZER_SCRIPT_PATH}`;

  return new Promise((resolve) => {
    const existing = document.querySelector(`script[data-valmar-probe="true"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(typeof window.Tokenizer === 'function'), { once: true });
      existing.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.setAttribute('data-valmar-probe', 'true');
    script.onload = () => resolve(typeof window.Tokenizer === 'function');
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

function probeTokenizerIframe(timeoutMs) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || typeof window.Tokenizer !== 'function') {
      resolve(false);
      return;
    }

    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    container.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;left:-9999px;top:-9999px';
    document.body.appendChild(container);

    let settled = false;
    const finish = (available) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      container.remove();
      resolve(available);
    };

    const timer = setTimeout(() => finish(false), timeoutMs);

    try {
      // Mirrors the production checkout iframe load path as closely as possible.
      // eslint-disable-next-line no-new
      new window.Tokenizer({
        url: VALMAR_ORIGIN,
        apikey: VALMAR_PUBLIC_API_KEY,
        container,
        onLoad: () => finish(true),
      });
    } catch (_) {
      finish(false);
    }
  });
}

async function runValmarProbe(timeoutMs = DEFAULT_PROBE_TIMEOUT_MS) {
  const halfTimeout = Math.max(1500, Math.floor(timeoutMs / 2));

  const [networkAvailable, iframeAvailable, tokenizerScriptAvailable] = await Promise.all([
    probeValmarNetwork(halfTimeout),
    probeValmarIframe(halfTimeout),
    loadTokenizerScript(),
  ]);

  if (!tokenizerScriptAvailable) {
    return false;
  }

  if (!networkAvailable && !iframeAvailable) {
    return false;
  }

  return probeTokenizerIframe(halfTimeout);
}

/**
 * Returns true when Valmar checkout assets appear reachable in this browser session.
 * Result is cached until explicitly cleared.
 */
export async function checkValmarAvailable({ timeoutMs = DEFAULT_PROBE_TIMEOUT_MS, forceRefresh = false } = {}) {
  if (typeof window === 'undefined') {
    return true;
  }

  if (!forceRefresh && availabilityCache !== null) {
    return availabilityCache;
  }

  if (!forceRefresh && inflightProbe) {
    return inflightProbe;
  }

  inflightProbe = runValmarProbe(timeoutMs)
    .then((available) => {
      availabilityCache = available;
      return available;
    })
    .finally(() => {
      inflightProbe = null;
    });

  return inflightProbe;
}

export function getValmarAvailabilityCache() {
  return availabilityCache;
}

export function clearValmarAvailabilityCache() {
  availabilityCache = null;
  inflightProbe = null;
}
